'use client';

import { ShowMore } from '@petsmart-ui/sparky';
import classnames from 'classnames';
import { type MouseEvent, useState } from 'react';

import { type Promotion } from '@/entities/product';

import { usePromoList } from './model';
import { PromoDrawer, PromoItem } from './ui';
import './styles.scss';

function getPromoListItemKey(promo: Promotion, variant: 'hide' | 'display', index: number): string {
    const id = promo.key ?? `order-${promo.order}`;
    return `${id}-${variant}-${index}`;
}

export const PromoList = () => {
    const { promosToDisplay, promosToHide, hasPromosDetails } = usePromoList();
    const [selectedPromo, setSelectedPromo] = useState<Nullable<Promotion>>(null);

    const className = classnames('promo-list', {
        'promo-list--no-hidden': !promosToHide.length,
    });

    // Since the `ShowMore` component doesn’t allow setting the button type, clicking the button causes the form to submit.
    // This hack will be fixed in the https://petsmart.atlassian.net/browse/SB-2307
    const handleClickCapture = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const btn = target.closest('button');
        if (btn && btn.type !== 'button') e.preventDefault();
    };

    return (
        <div onClickCapture={handleClickCapture}>
            <ShowMore
                iconPosition="after"
                className={className}
                buttonVariant="link"
                iconName="chevron-down"
                hideButtonText="Show less"
                data-testid="promo-list-hidden"
                buttonText={`Show more offers (${promosToHide.length})`}
                hiddenContent={promosToHide.map((promo, index) => (
                    <PromoItem
                        key={getPromoListItemKey(promo, 'hide', index)}
                        promo={promo}
                        onClickAction={() => setSelectedPromo(promo)}
                    />
                ))}
            >
                {promosToDisplay.map((promo, index) => (
                    <PromoItem
                        key={getPromoListItemKey(promo, 'display', index)}
                        promo={promo}
                        onClickAction={() => setSelectedPromo(promo)}
                    />
                ))}

                {hasPromosDetails && (
                    <PromoDrawer selectedPromo={selectedPromo} onCloseAction={() => setSelectedPromo(null)} />
                )}
            </ShowMore>
        </div>
    );
};
